<template>
  <DsCard is="form" id="login-form" class="tw-w-[400px]" @submit.prevent="login">
    <!-- hack for SEO to have a title on each page -->
    <h1 class="tw-sr-only">Login</h1>
    <p class="tw-text-center tw-ds-text-lg--semibold">
      {{ $t('Login.visibleTitle') }}
    </p>
    <p class="tw-text-center tw-ds-text-sm tw-mb-6 tw-mt-2">
      {{ $t('Login.visibleSubtitle') }}
    </p>
    <DsError
      v-if="microsoftError"
      :error="microsoftError"
      :translate-error-title="() => $t('Login.microsoftErrorTitle')"
      :translate-error="translateAuthenticationErrorMessage"
      :help-link="support.MSSO"
    />

    <MicrosoftSignInButton
      full
      :loading="microsoftSignInLoading"
      :disabled="disableButtons"
      @click="microsoftLogin"
    />

    <AuthenticationProviderDivider />

    <DsError v-if="mailError" :error="mailError" :translate-error="translateError" />
    <div class="tw-flex tw-flex-col tw-space-y-3">
      <DsField
        v-model="credentials.email"
        :label="$t('objects.communication.email')"
        type="email"
        required
      />
      <PasswordInput v-model="credentials.password" />
    </div>

    <div class="tw-text-start tw-mb-4">
      <DsButton
        hierarchy="link-grey"
        class="tw-pl-0"
        is-link
        :to="{
          name: Routes.Unauthenticated.ResetPassword,
        }"
      >
        <span>{{ t('Login.forgotPassword') }}</span>
      </DsButton>
    </div>
    <div class="tw-mt-6">
      <DsButton
        id="login-button"
        :disabled="disableButtons"
        :loading="loading"
        data-cy="button-submit"
        type="submit"
        full
        size="xl"
      >
        {{ t('Login.signIn') }}
      </DsButton>
    </div>
  </DsCard>
</template>

<script setup lang="ts">
import { onMounted, Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Router, useRoute, useRouter } from 'vue-router';

import PasswordInput from '@/components/inputs/PasswordInput.vue';
import AuthenticationProviderDivider from '@/components/unauthenticated/AuthenticationProviderDivider.vue';
import MicrosoftSignInButton from '@/components/unauthenticated/MicrosoftSignInButton.vue';
import { useLoginErrorTranslation } from '@/helpers/errorTranslation/login';
import { isString } from '@/helpers/isString';
import { Routes } from '@/router/helpers/routeNames';
import { translateAuthenticationErrorMessage } from '@/services/authentication/interfaces';
import { useAuthenticationService } from '@/services/authentication/useAuthenticationService';
import { useUserStore } from '@/services/store/user/store';
import { support } from '@/utils/pages';

interface LoginCredentials {
  email: string;
  password: string;
}

const route = useRoute();
const router = useRouter();
const authenticationService = useAuthenticationService();
const userStore = useUserStore();

onMounted(() => {
  userStore.reset();
});

const credentials = ref<LoginCredentials>({
  email: '',
  password: '',
});

const i18n = useI18n();
const { t } = i18n;
const { translateError } = useLoginErrorTranslation(i18n);

const microsoftSignInLoading = ref(false);
const loading = ref(false);

const disableButtons = computed(() => microsoftSignInLoading.value || loading.value);

const mailError = ref(null) as Ref<Error | null | string>;
const microsoftError = ref(null) as Ref<Error | null | string>;

function isValidRedirect(router: Router, value: string) {
  const resolvedRoute = router.resolve(value);
  return !!resolvedRoute.matched.length;
}

const onSuccessfulLogin = () => {
  const redirect = isString(route.query.login_redirect)
    ? decodeURI(route.query.login_redirect)
    : null;

  if (redirect && isValidRedirect(router, redirect)) {
    try {
      router.push(redirect);
    } catch {
      router.push({ name: Routes.Base.PickTenant });
    }
  } else {
    router.push({ name: Routes.Base.PickTenant });
  }
};

const resetErrors = () => {
  microsoftError.value = null;
  mailError.value = null;
};

const login = (): void => {
  loading.value = true;
  resetErrors();
  authenticationService
    .login(credentials.value.email, credentials.value.password)
    .then(() => {
      onSuccessfulLogin();
    })
    .catch((_error) => {
      loading.value = false;
      mailError.value = _error;
    });
};

const microsoftLogin = async () => {
  resetErrors();
  microsoftSignInLoading.value = true;
  try {
    await authenticationService.loginWithMicrosoft();
    onSuccessfulLogin();
  } catch (e) {
    microsoftError.value = e as Error;
  } finally {
    microsoftSignInLoading.value = false;
  }
};
</script>

<style scoped>
a:link {
  text-decoration: none;
  color: inherit;
}

a:visited {
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

a:active {
  text-decoration: underline;
}
</style>
